import { useCallback, useEffect, useRef, useState } from "react";
import { figureSvgUrl, parseFigureError, sourceHash, svgDataUrl, type FigureError } from "@/lib/tikzFigure";
import { DRAW_SAMPLES, figureDraftUrl, redrawDelay, type DraftState } from "@/lib/tikzAuthoring";
import { NOT_SAVED_RETRY_MS } from "./useTikzDrawing";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface TikzDraft {
  state: DraftState;
  /** drawn: the server's X-Draw-Ms (else the measured round trip). */
  drawMs: number | null;
  /** drawing: how long this draw has been going, ticking. */
  elapsedMs: number;
  /**
   * The drawing on show: the latest one that drew (60 % while a newer source
   * draws), or with an error the last version that drew (40 %).
   */
  svg: string | null;
  error: FigureError | null;
  /** The newest source that drew and its drawing, for the canvas behind. */
  good: { hash: string; svg: string } | null;
  /** The pause after typing before a redraw: 600 ms, or 1 s once draws measure slow. */
  redrawMs: number;
  /** ⌘S: draw the current source (or the one given) now, not after the pause. */
  drawNow: (source?: string) => void;
}

const UNAVAILABLE: FigureError = { ...parseFigureError({}), kind: "unavailable", message: "" };

/** 413: over the route's 200,000 characters. The author's to fix, so not "our side". */
export const TOO_LONG_MESSAGE = "This source is too long to draw";
const TOO_LONG: FigureError = { ...parseFigureError({}), kind: "tex", message: TOO_LONG_MESSAGE };

function isAbort(e: unknown): boolean {
  return !!e && typeof e === "object" && (e as { name?: string }).name === "AbortError";
}

/**
 * The split view's drawing: the source in the editor (not the saved one) is
 * sent to the draft route 600 ms after typing stops (1 s once draws measure
 * slow). A newer source aborts the older request, which cancels its compile on
 * the server, so only the latest source is ever shown.
 */
export function useTikzDraft(opts: {
  fetchWithAuth: Fetch;
  documentId: string | null | undefined;
  blockId: string | null | undefined;
  source: string;
  /** The canvas's drawing, shown until the first draft answers. */
  initialSvg?: string | null;
}): TikzDraft {
  const { fetchWithAuth, documentId, blockId, source } = opts;
  const [state, setState] = useState<DraftState>(source.trim() ? "drawing" : "empty");
  const [drawMs, setDrawMs] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [svg, setSvg] = useState<string | null>(opts.initialSvg ?? null);
  const [error, setError] = useState<FigureError | null>(null);
  const [good, setGood] = useState<{ hash: string; svg: string } | null>(null);

  const controller = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const samples = useRef<number[]>([]);
  const goodSvg = useRef<string | null>(opts.initialSvg ?? null);
  const latest = useRef(source);
  latest.current = source;
  const startedAt = useRef(0);
  /** The source last sent (or being sent), so its own echo doesn't send it again. */
  const requested = useRef<string | null>(null);

  const draw = useCallback(
    async (src: string) => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      controller.current?.abort();
      controller.current = null;
      requested.current = src;
      if (!src.trim()) {
        setState("empty");
        setError(null);
        return;
      }
      if (!documentId || !blockId) return;
      const ctl = new AbortController();
      controller.current = ctl;
      startedAt.current = Date.now();
      setElapsedMs(0);
      setState("drawing");
      try {
        // A figure just inserted (a template, Insert ▸ TikZ) may not have reached the server yet:
        // the draft answers 404 until autosave has written the block. Wait and ask again, as the
        // canvas does (NOT_SAVED_RETRY_MS); it showed "Didn't draw" and stayed there (16 Oct).
        let res: Response;
        for (let attempt = 0; ; attempt++) {
          res = await fetchWithAuth(figureDraftUrl(documentId, blockId), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ source: src }),
            signal: ctl.signal,
          });
          if (ctl.signal.aborted) return;
          if (res.status !== 404 || attempt >= NOT_SAVED_RETRY_MS.length) break;
          await new Promise((r) => setTimeout(r, NOT_SAVED_RETRY_MS[attempt]));
          if (ctl.signal.aborted) return;
        }
        if (res.ok) {
          const text = await res.text();
          if (ctl.signal.aborted) return;
          const header = Number(res.headers?.get?.("X-Draw-Ms"));
          const ms = Number.isFinite(header) && header > 0 ? header : Date.now() - startedAt.current;
          samples.current = [...samples.current, ms].slice(-DRAW_SAMPLES);
          const url = svgDataUrl(text);
          goodSvg.current = url;
          setSvg(url);
          setGood({ hash: sourceHash(src), svg: url });
          setDrawMs(ms);
          setError(null);
          setState("drawn");
          return;
        }
        // A newer draft of this block replaced this one on the server: it
        // answers instead, so this answer changes nothing.
        if (res.status === 409) return;
        const e =
          res.status === 422
            ? parseFigureError(await res.json().catch(() => ({})))
            : res.status === 413
              ? TOO_LONG
              : UNAVAILABLE;
        if (ctl.signal.aborted) return;
        setError(e);
        setState("error");
        // The last version that drew: this session's, else the server's.
        if (goodSvg.current) {
          setSvg(goodSvg.current);
        } else if (e.hasLastGood) {
          setSvg(null);
          const lg = await fetchWithAuth(figureSvgUrl(documentId, blockId, { lastGood: true }), { signal: ctl.signal });
          if (!ctl.signal.aborted && lg.ok) {
            const text = await lg.text();
            if (!ctl.signal.aborted) {
              goodSvg.current = svgDataUrl(text);
              setSvg(goodSvg.current);
            }
          }
        } else {
          setSvg(null);
        }
      } catch (e) {
        if (isAbort(e) || ctl.signal.aborted) return;
        setError(UNAVAILABLE);
        setState("error");
        setSvg(goodSvg.current);
      } finally {
        if (controller.current === ctl) controller.current = null;
      }
    },
    [fetchWithAuth, documentId, blockId],
  );

  // The opening source draws at once; every later one after the pause.
  const opened = useRef(false);
  useEffect(() => {
    if (!opened.current) {
      opened.current = true;
      void draw(source);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (source === requested.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void draw(latest.current);
    }, redrawDelay(samples.current));
  }, [source, draw]);

  // Over 2 s, the status shows the elapsed time.
  useEffect(() => {
    if (state !== "drawing") return;
    const t = setInterval(() => setElapsedMs(Date.now() - startedAt.current), 500);
    return () => clearInterval(t);
  }, [state]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      controller.current?.abort();
      // Mounted again (StrictMode does this at once): the source draws again at once.
      requested.current = null;
      opened.current = false;
    },
    [],
  );

  const drawNow = useCallback((src?: string) => void draw(src ?? latest.current), [draw]);

  // Read at render: the samples change with each draw, which sets drawMs.
  const redrawMs = redrawDelay(samples.current);
  return { state, drawMs, elapsedMs, svg, error, good, redrawMs, drawNow };
}
